"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

interface RouteStats {
    km: string;
    description: string;
}

interface RouteCardProps {
    imageSrc: string;
    logoSrc?: string;
    title?: string;
    type: "Offroad" | "Onroad";
    description: string;
    proStats: RouteStats;
    liteStats: RouteStats;
    statusLabel?: string; // e.g. "Plazas Agotadas"
    waitlist?: { label: string; href: string };
    hideInfoButton?: boolean;
}

export default function RouteCard({
    imageSrc,
    logoSrc,
    title,
    type,
    description,
    proStats,
    liteStats,
    statusLabel,
    waitlist,
    hideInfoButton = false,
}: RouteCardProps) {
    const [activeTab, setActiveTab] = useState<"pro" | "lite">("pro");

    const isOffroad = type === "Offroad";
    const accentColor = isOffroad ? "text-primary" : "text-secondary";
    const borderColor = isOffroad ? "border-primary" : "border-secondary";
    const bgColor = isOffroad ? "bg-primary" : "bg-secondary";

    return (
        <div className={`group bg-white dark:bg-zinc-900 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300`}>
            {/* Header Image */}
            <div className="relative h-64 overflow-hidden">
                <Image
                    src={imageSrc}
                    alt={type}
                    fill
                    className="object-cover group-hover:scale-110 transition-transform duration-700"
                />
                {/* Dark gradient overlay removed */}
                <div className="absolute bottom-4 left-4">
                    <span className={`inline-block px-3 py-1 ${bgColor} text-black font-bold uppercase tracking-wider text-xs`}>
                        {type}
                    </span>
                </div>
            </div>

            <div className="p-8">
                {/* Title / Logo Area */}
                <div className="mb-6">
                    {logoSrc ? (
                        <div className="h-16 w-auto relative flex items-center mb-4">
                            <img src={logoSrc} alt={type} className="h-full object-contain object-left" />
                        </div>
                    ) : (
                        <div className="flex items-center gap-3 mb-4">
                            <span className={`material-icons ${accentColor} text-3xl`}>
                                {isOffroad ? "terrain" : "timeline"}
                            </span>
                            <h3 className="font-display uppercase text-3xl text-black dark:text-white leading-none">
                                {title}
                            </h3>
                        </div>
                    )}

                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed border-l-2 border-gray-200 dark:border-gray-700 pl-4">
                        {description}
                    </p>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
                    <button
                        onClick={() => setActiveTab("pro")}
                        className={`flex-1 pb-3 text-center font-display uppercase tracking-wider text-lg transition-colors relative ${activeTab === "pro"
                            ? `text-black dark:text-white`
                            : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            }`}
                    >
                        PRO
                        {activeTab === "pro" && (
                            <span className={`absolute bottom-0 left-0 w-full h-1 ${bgColor}`} />
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab("lite")}
                        className={`flex-1 pb-3 text-center font-display uppercase tracking-wider text-lg transition-colors relative ${activeTab === "lite"
                            ? `text-black dark:text-white`
                            : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            }`}
                    >
                        LITE
                        {activeTab === "lite" && (
                            <span className={`absolute bottom-0 left-0 w-full h-1 ${bgColor}`} />
                        )}
                    </button>
                </div>

                {/* Content Area */}
                <div className="min-h-[120px]">
                    <div className="flex justify-between items-baseline mb-3">
                        <h4 className={`font-bold text-xl uppercase ${accentColor}`}>
                            {activeTab === "pro" ? "Recorrido PRO" : "Recorrido LITE"}
                        </h4>
                        <span className="text-2xl font-display text-gray-800 dark:text-gray-200">
                            {activeTab === "pro" ? proStats.km : liteStats.km}
                        </span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                        {activeTab === "pro" ? proStats.description : liteStats.description}
                    </p>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-800 flex justify-between items-center flex-wrap gap-4">
                    <span className={`text-xs uppercase tracking-widest ${statusLabel?.toLowerCase().includes("agotadas") ? "text-red-500 font-bold" : "text-gray-400"}`}>
                        {statusLabel || "Plazas Limitadas"}
                    </span>
                    <div className="flex gap-3">
                        {waitlist && (
                            <a
                                href={waitlist.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`px-4 py-2 ${bgColor} text-black hover:opacity-90 transition-colors uppercase text-xs font-bold tracking-widest rounded-sm`}
                            >
                                {waitlist.label}
                            </a>
                        )}
                        {!hideInfoButton && (
                            <Link
                                href="/rutas"
                                className={`px-6 py-2 border ${borderColor} ${accentColor} hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors uppercase text-xs font-bold tracking-widest`}
                            >
                                Más Info
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
